import React, { useCallback, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';

import { IconButton, Tooltip } from '@mui/material';

import { DateFormat } from '../../../../../../utils/date';
import { formatRecurrenceSummary } from '../../../../../../utils/recurrence';
import CoachReminderNotification from '../../../../../../Model/CoachReminderNotification';
import { InfoTag, LightTag } from '../../../../../../components/Tags';
import ConfirmDialog from '../../../../../components/ConfirmDialog';
import ReminderForm from '../ReminderForm';

import {
  ItemContainer,
  DateColumn,
  ContentColumn,
  MessageDate,
  MessageTime,
  MessageText,
  MessageSubtitle,
  ActionsContainer,
} from './styles';
import texts from './texts.json';

const ScheduledReminderItem = ({
  reminderNotificationDoc,
}) => {
  const {
    content,
    sendAt,
    recurrence,
  } = reminderNotificationDoc;

  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const handleSubmit = useCallback(async ({ content: nextContent, resolvedSendAt, rule }) => {
    await reminderNotificationDoc.updateContentAndSchedule(nextContent, resolvedSendAt, rule);
    setIsEditing(false);
  }, [reminderNotificationDoc]);

  const onDelete = useCallback(async () => {
    await reminderNotificationDoc.delete();
    setIsDeleteConfirmOpen(false);
  }, [reminderNotificationDoc]);

  if (isEditing) {
    return (
      <ItemContainer>
        <ReminderForm
          initialContent={content}
          initialSendAt={sendAt}
          initialRecurrence={recurrence}
          onSubmit={handleSubmit}
          onCancel={() => setIsEditing(false)}
          submitLabel={texts.button.save}
        />
      </ItemContainer>
    );
  }

  const sendMoment = moment(sendAt).local();
  const summary = recurrence ? formatRecurrenceSummary(recurrence) : null;

  return (
    <>
      <ItemContainer>
        <DateColumn>
          {recurrence
            ? <InfoTag>{texts.recurringTag}</InfoTag>
            : <LightTag>{texts.oneTimeTag}</LightTag>}
          <MessageDate>{sendMoment.format(DateFormat.DATE_FORMAT_SLASH)}</MessageDate>
          <MessageTime>{sendMoment.format('h:mm A')}</MessageTime>
        </DateColumn>
        <ContentColumn>
          <MessageText>{content}</MessageText>
          {summary && <MessageSubtitle>{summary}</MessageSubtitle>}
        </ContentColumn>
        <ActionsContainer>
          <Tooltip title={texts.tooltip.delete}>
            <IconButton onClick={() => setIsDeleteConfirmOpen(true)}>
              <ClearIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={texts.tooltip.edit}>
            <IconButton onClick={() => setIsEditing(true)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
        </ActionsContainer>
      </ItemContainer>
      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onConfirm={onDelete}
        onCancel={() => setIsDeleteConfirmOpen(false)}
        dialogTexts={{
          title: texts.deleteConfirm,
          confirm: texts.button.delete,
          cancel: texts.button.cancel,
        }}
      />
    </>
  );
};

ScheduledReminderItem.propTypes = {
  reminderNotificationDoc: PropTypes.instanceOf(CoachReminderNotification).isRequired,
};

export default observer(ScheduledReminderItem);
